import React, { useMemo } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from './rn';
import { fontSize, spacing, type ThemeColors, radius } from '../theme';
import { useTheme } from '../theme/ThemeContext';

type Props = {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

// Onboarding's primary actions share geometry, contrast and loading semantics.
// Keep the label in layout while loading, including its wrapped/scaled height.
export default function PrimaryActionButton({
  label, onPress, disabled = false, loading = false, style, testID,
}: Props) {
  const colors = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const inactive = disabled || loading;
  const muted = disabled && !loading;

  return (
    <TouchableOpacity
      testID={testID}
      style={[styles.button, muted && styles.disabled, style]}
      disabled={inactive}
      onPress={() => { if (!inactive) onPress(); }}
      activeOpacity={0.7}
      accessible
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled: inactive, busy: loading }}>
      <Text
        testID={testID ? `${testID}-label` : undefined}
        style={[styles.label, muted && styles.disabledLabel, loading && styles.hiddenLabel]}
        accessible={false}>
        {label}
      </Text>
      {loading && (
        <View
          style={styles.spinner}
          pointerEvents="none"
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants">
          <ActivityIndicator color={colors.white} accessible={false} />
        </View>
      )}
    </TouchableOpacity>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  button: {
    minHeight: 52,
    minWidth: 0,
    alignSelf: 'stretch',
    paddingHorizontal: spacing.md,
    paddingVertical: 14,
    borderRadius: radius.control,
    backgroundColor: colors.black,
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabled: { backgroundColor: colors.gray200 },
  label: {
    color: colors.white,
    fontSize: fontSize.md,
    fontWeight: '600',
    lineHeight: 24,
    textAlign: 'center',
    flexShrink: 1,
    alignSelf: 'stretch',
  },
  disabledLabel: { color: colors.gray600 },
  hiddenLabel: { opacity: 0 },
  spinner: { ...StyleSheet.absoluteFill, alignItems: 'center', justifyContent: 'center' },
});
